import { useEffect, useState, useMemo } from 'react';
import {
  workerApi, workerKitApi, workerSiteAssignmentApi,
  siteApi, stationeryApi, salaryDeductionApi,
  permissionGroupApi, materialUnitApi,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MaterialInfoPanel from '../../components/common/MaterialInfoPanel';
import SearchableSelect from '../../components/common/SearchableSelect';
import SiteCorrections from './SiteCorrections';
import { buildCycles } from '../../constants/deductionCycles';

const fmtCurrency = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


export default function WorkerList() {
  const { user, hasPermission, isSiteRestricted: isStorekeeper } = useAuth();

  // ── Core data ──────────────────────────────────────────────────────────────
  const [workers, setWorkers]     = useState([]);
  const [sites, setSites]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState({ siteId: '' });
  const [error, setError]         = useState('');
  const [workerTypeTab, setWorkerTypeTab] = useState('sptl');
  const [workerSearch, setWorkerSearch]   = useState('');

  // ── Permission Groups (for detail modal PG tab) ───────────────────────────
  const [allGroups, setAllGroups]   = useState([]);
  const [workerPG, setWorkerPG]     = useState(null); // { linkedUserId, group }
  const [pgGroupId, setPgGroupId]   = useState('');
  const [pgLoading, setPgLoading]   = useState(false);
  const [pgSaving, setPgSaving]     = useState(false);
  const [pgError, setPgError]       = useState('');

  // ── Material ID lifecycle history modal (same pattern as InventoryPage.jsx) ──
  const [historyModal, setHistoryModal] = useState(null); // { unit, events } | 'loading'
  const openMaterialHistory = async (materialUnitId) => {
    setHistoryModal('loading');
    try {
      const res = await materialUnitApi.history(materialUnitId);
      setHistoryModal(res.data.data);
    } catch (err) {
      setHistoryModal(null);
      setError(err.response?.data?.message || 'Error loading material history');
    }
  };

  // Locked means: this group can never be reassigned from here at all —
  // either it's restricted outright (Admin Access), or it's a full-access-
  // style group the current viewer doesn't hold themselves.
  const pgCurrentGroup = workerPG?.group;
  const isPgLocked = !!pgCurrentGroup && (
    pgCurrentGroup.isSynthetic
    || pgCurrentGroup.isRestricted
    || (pgCurrentGroup.isSystem && !(pgCurrentGroup.permissions || []).every((pe) => hasPermission(pe.permission)))
  );

  // Mirrors the same restriction the backend already enforces (workers.js's
  // setPermissionGroup, permissionGroups.js's escalation guard): a group can
  // configure a specific "assignableGroups" list, and its members are then
  // limited to assigning only those groups to a worker — an empty list means
  // unrestricted (assign any group), matching existing behavior. Previously
  // this UI showed every group regardless, so a restricted actor could pick
  // an option here that the backend would then reject. Super_admin
  // (permissions === 'ALL') is never restricted, same as backend.
  const actorIsSuperAdmin = user?.permissions === 'ALL';
  const actorGroup = actorIsSuperAdmin
    ? null
    : allGroups.find((g) => g.id === user?.permissionGroupId) || allGroups.find((g) => g.isDefault);
  const actorAssignableIds = (!actorIsSuperAdmin && actorGroup && !actorGroup.isSystem && (actorGroup.assignableGroups || []).length > 0)
    ? new Set(actorGroup.assignableGroups.map((ag) => ag.id))
    : null; // null = unrestricted

  // ── Site Assignments ───────────────────────────────────────────────────────
  const [assignmentWorker, setAssignmentWorker] = useState(null);
  const [assignments, setAssignments]           = useState([]);
  const [assignSiteId, setAssignSiteId]         = useState('');

  // ── Transfer ───────────────────────────────────────────────────────────────
  const [transferWorker, setTransferWorker] = useState(null);
  const [transferForm, setTransferForm]     = useState({ fromSiteId: '', toSiteId: '' });

  // ── Bulk Assign (paste many names/employee IDs, assign them all at once) ──
  const [bulkAssignOpen, setBulkAssignOpen]   = useState(false);
  const [bulkPasteText, setBulkPasteText]     = useState('');
  const [bulkSiteId, setBulkSiteId]           = useState('');
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkMatchedWorkers, setBulkMatchedWorkers] = useState([]);
  const [bulkNotFoundTokens, setBulkNotFoundTokens] = useState([]);
  const [bulkSubmitting, setBulkSubmitting]   = useState(false);
  const [bulkResult, setBulkResult]           = useState(null);
  const [bulkError, setBulkError]             = useState('');

  // ── Worker Detail (read-only) ──────────────────────────────────────────────
  const [detailWorker, setDetailWorker]               = useState(null);
  const [detailTab, setDetailTab]                     = useState('overview');
  const [workerKits, setWorkerKits]                   = useState([]);
  const [workerStatIssuances, setWorkerStatIssuances] = useState([]);
  const [allWorkerKits, setAllWorkerKits]             = useState([]);
  const [allStatIssuances, setAllStatIssuances]       = useState([]);
  const [workerDeductions, setWorkerDeductions]       = useState([]);
  const [workerAssignHistory, setWorkerAssignHistory] = useState([]);
  const [loadingDetail, setLoadingDetail]             = useState(false);
  const deductionCycles = useMemo(() => buildCycles(6), []);
  const [deductionCycleFilter, setDeductionCycleFilter] = useState('current'); // 'current' | 'all' | a cycleStart date string

  // ── Load workers + sites ───────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      // Subcontractors have their own dedicated Subcontractor Management page —
      // excluded here so they don't also show up as a confusing duplicate
      // under Master Data's Workers panel.
      const params = { excludeSubcontractors: 'true' };
      if (filter.siteId) params.siteId = filter.siteId;
      const [w, s, pg] = await Promise.all([workerApi.list(params), siteApi.list(), permissionGroupApi.list()]);
      setWorkers(w.data.data);
      setSites(s.data.data);
      setAllGroups(pg.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  // ── Permission Group tab ───────────────────────────────────────────────────
  useEffect(() => {
    if (detailTab !== 'permissions' || !detailWorker) return;
    let cancelled = false;
    setPgLoading(true);
    setPgError('');
    workerApi.getPermissionGroup(detailWorker.id)
      .then((res) => {
        if (cancelled) return;
        const data = res.data.data;
        setWorkerPG(data);
        setPgGroupId(data?.group?.id ? String(data.group.id) : '');
      })
      .catch(() => { if (!cancelled) setPgError('Failed to load permission group'); })
      .finally(() => { if (!cancelled) setPgLoading(false); });
    return () => { cancelled = true; };
  }, [detailTab, detailWorker]); // eslint-disable-line

  const handleSavePG = async () => {
    setPgSaving(true);
    setPgError('');
    try {
      await workerApi.setPermissionGroup(detailWorker.id, {
        groupId: pgGroupId ? parseInt(pgGroupId) : null,
      });
      // Refresh list so the Permission Group column updates, then close —
      // there's nothing left to do in this panel after a successful save.
      load();
      setDetailWorker(null);
    } catch (err) {
      setPgError(err.response?.data?.message || 'Failed to save permission group');
    } finally {
      // Must always reset, success or failure — closing the modal here
      // doesn't unmount WorkerList itself, so a stale `true` would leave the
      // Save button permanently disabled ("Saving…") the next time any
      // worker's permission panel is opened.
      setPgSaving(false);
    }
  };

  // ── Worker Detail (read-only) ──────────────────────────────────────────────
  const openDetail = async (worker, initialTab = 'overview') => {
    setDetailWorker(worker);
    setDetailTab(initialTab);
    setLoadingDetail(true);
    setWorkerPG(null);
    setPgGroupId('');
    setPgError('');
    setError('');
    setDeductionCycleFilter('current');
    try {
      // Stationery reads are gated server-side by stationery_view (see
      // InventoryPage.jsx's canViewStationery) — fetch only when held, or
      // Promise.all rejects on the 403 and this whole detail panel (kits,
      // deductions, site history — all of which loaded fine) fails to render.
      const canSeeStationery = hasPermission('stationery_view');
      const [kitsActiveRes, statActiveRes, kitsAllRes, statAllRes, deducRes, assignRes] = await Promise.all([
        workerKitApi.list({ workerId: worker.id, activeOnly: 'true' }),
        canSeeStationery ? stationeryApi.listIssuances({ workerId: worker.id, isReturned: false }) : Promise.resolve({ data: { data: [] } }),
        workerKitApi.list({ workerId: worker.id }),
        canSeeStationery ? stationeryApi.listIssuances({ workerId: worker.id }) : Promise.resolve({ data: { data: [] } }),
        salaryDeductionApi.list({ workerId: worker.id }),
        workerSiteAssignmentApi.list({ workerId: worker.id }),
      ]);
      setWorkerKits(kitsActiveRes.data.data);
      setWorkerStatIssuances(statActiveRes.data.data);
      setAllWorkerKits(kitsAllRes.data.data);
      setAllStatIssuances(statAllRes.data.data);
      setWorkerDeductions(deducRes.data.data || []);
      setWorkerAssignHistory(assignRes.data.data || []);
    } catch {
      setWorkerKits([]);
      setWorkerStatIssuances([]);
      setAllWorkerKits([]);
      setAllStatIssuances([]);
      setWorkerDeductions([]);
      setWorkerAssignHistory([]);
    }
    setLoadingDetail(false);
  };

  // ── Site Assignments ───────────────────────────────────────────────────────
  const openAssignments = async (worker) => {
    setAssignmentWorker(worker);
    setAssignSiteId('');
    setError('');
    try {
      const res = await workerSiteAssignmentApi.list({ workerId: worker.id });
      setAssignments(res.data.data);
    } catch { setAssignments([]); }
  };

  const handleAddAssignment = async () => {
    if (!assignSiteId) return;
    setError('');
    try {
      await workerSiteAssignmentApi.create({ workerId: assignmentWorker.id, siteId: parseInt(assignSiteId) });
      setAssignSiteId('');
      const res = await workerSiteAssignmentApi.list({ workerId: assignmentWorker.id });
      setAssignments(res.data.data);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error adding assignment'); }
  };

  const handleRemoveAssignment = async (workerId, siteId) => {
    setError('');
    try {
      await workerSiteAssignmentApi.remove(workerId, siteId);
      const res = await workerSiteAssignmentApi.list({ workerId });
      setAssignments(res.data.data);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error removing assignment'); }
  };

  // ── Transfer ───────────────────────────────────────────────────────────────
  const openTransfer = (worker) => {
    const firstSiteId = worker.siteAssignments?.[0]?.siteId ?? '';
    setTransferForm({ fromSiteId: String(firstSiteId), toSiteId: '' });
    setTransferWorker(worker);
    setError('');
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await workerSiteAssignmentApi.transfer({
        workerId:   transferWorker.id,
        fromSiteId: parseInt(transferForm.fromSiteId),
        toSiteId:   parseInt(transferForm.toSiteId),
      });
      setTransferWorker(null);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Transfer failed'); }
  };

  // ── Bulk Assign ────────────────────────────────────────────────────────────
  // Splits pasted text on newlines/commas, then matches each token against
  // name OR employeeId (exact/prefix/substring, same fields smartSearch
  // already uses) — a token can match more than one worker (e.g. a common
  // first name), all of which show up for the caller to individually
  // include/exclude, and a token that matches nothing surfaces separately
  // so pasted-but-unrecognized entries are never silently dropped.
  const openBulkAssign = () => {
    setBulkAssignOpen(true);
    setBulkPasteText('');
    setBulkSiteId('');
    setBulkMatchedWorkers([]);
    setBulkNotFoundTokens([]);
    setBulkSelectedIds(new Set());
    setBulkResult(null);
    setBulkError('');
  };

  const findBulkMatches = () => {
    const tokens = [...new Set(
      bulkPasteText.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean)
    )];
    const matchedMap = new Map();
    const notFound = [];
    for (const token of tokens) {
      const q = token.toLowerCase();
      const hits = workers.filter((w) => {
        const name  = (w.name || '').toLowerCase();
        const empId = (w.employeeId || '').toLowerCase();
        return empId === q || empId.includes(q) || name === q || name.includes(q);
      });
      if (hits.length === 0) notFound.push(token);
      else hits.forEach((w) => matchedMap.set(w.id, w));
    }
    const matched = [...matchedMap.values()];
    setBulkMatchedWorkers(matched);
    setBulkNotFoundTokens(notFound);
    setBulkSelectedIds(new Set(matched.map((w) => w.id))); // select all by default
    setBulkResult(null);
    setBulkError('');
  };

  const toggleBulkSelected = (id) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleBulkSelectAll = () => {
    setBulkSelectedIds((prev) =>
      prev.size === bulkMatchedWorkers.length ? new Set() : new Set(bulkMatchedWorkers.map((w) => w.id))
    );
  };

  const handleBulkAssignSubmit = async () => {
    if (!bulkSiteId || bulkSelectedIds.size === 0) return;
    setBulkSubmitting(true);
    setBulkError('');
    try {
      const res = await workerSiteAssignmentApi.bulkCreate({
        workerIds: [...bulkSelectedIds],
        siteId: parseInt(bulkSiteId),
      });
      setBulkResult(res.data.data);
      load();
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Bulk assignment failed');
    }
    setBulkSubmitting(false);
  };

  if (loading) return <LoadingSpinner />;

  // ── Derived ────────────────────────────────────────────────────────────────
  const assignedSiteIds = assignments.map((a) => a.siteId);
  const unassignedSites = sites.filter((s) => !assignedSiteIds.includes(s.id));

  const byType = workers.filter((w) =>
    workerTypeTab === 'sptl' ? w.employeeType === 'SPTL' : w.employeeType === 'CONTRACT'
  );

  // `workers` is already scoped by the site filter (fetched per filter.siteId
  // in load()), so these counts reflect the current site filter automatically
  // — including "All Sites", where filter.siteId is empty and every worker
  // is included. Deliberately independent of the search box, which only
  // narrows what's rendered in the table below.
  const sptlCount     = workers.filter((w) => w.employeeType === 'SPTL').length;
  const contractCount = workers.filter((w) => w.employeeType === 'CONTRACT').length;

  const smartSearch = (list, query) => {
    const q = query.toLowerCase().trim();
    if (!q) return list;
    const tokens = q.split(/\s+/);
    const score = (w) => {
      const name  = (w.name || '').toLowerCase();
      const empId = (w.employeeId || '').toLowerCase();
      const parts = name.split(/\s+/);
      let s = 0;
      if (empId === q)              s += 100;
      else if (empId.startsWith(q)) s += 85;
      else if (empId.includes(q))   s += 55;
      if (name === q)               s += 100;
      else if (name.startsWith(q))  s += 90;
      else if (name.includes(q))    s += 65;
      for (const tok of tokens)
        for (const part of parts)
          if (part === tok)              s += 45;
          else if (part.startsWith(tok)) s += 28;
          else if (part.includes(tok))   s += 12;
      return s;
    };
    return list
      .map((w) => ({ w, s: score(w) }))
      .filter(({ s }) => s > 0)
      .sort((a, b) => b.s - a.s)
      .map(({ w }) => w);
  };

  const displayedWorkers = smartSearch(byType, workerSearch);

  const now = new Date();
  const matIssued     = workerKits;
  const durableIss    = workerStatIssuances.filter((i) => i.item?.category === 'DURABLE');
  const consumableIss = workerStatIssuances.filter((i) => i.item?.category === 'CONSUMABLE');

  // ── Deductions tab derived values ─────────────────────────────────────────
  const currentCycleStart = deductionCycles[0]?.cycleStart;
  const currentCycleDeductions = workerDeductions.filter(
    (d) => d.cycleStart && new Date(d.cycleStart).toISOString().slice(0, 10) === currentCycleStart
  );
  const currentCycleTotal = currentCycleDeductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  const allTimeTotal = workerDeductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  const displayedDeductions = deductionCycleFilter === 'all'
    ? workerDeductions
    : deductionCycleFilter === 'current'
      ? currentCycleDeductions
      : workerDeductions.filter(
          (d) => d.cycleStart && new Date(d.cycleStart).toISOString().slice(0, 10) === deductionCycleFilter
        );

  return (
    <div>
      <PageHeader
        title="Workers"
        subtitle="View workers, assign to sites, transfer between sites, and manage permission groups"
      />

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>
      )}

      {/* ── Type Tab Bar ──────────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { key: 'sptl',     label: `SPTL Employees (${sptlCount})` },
          { key: 'contract', label: `Contract Workers (${contractCount})` },
          ...(hasPermission('site_correction_review') ? [{ key: 'corrections', label: 'Site Corrections' }] : []),
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setWorkerTypeTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              workerTypeTab === t.key
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {workerTypeTab === 'corrections' ? (
        <SiteCorrections embedded />
      ) : (
      <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {!isStorekeeper && (
          <div className="w-48">
            <SearchableSelect
              options={[
                { id: '', label: 'All Sites' },
                { id: 'unassigned', label: '— Site not assigned —' },
                ...sites.map((s) => ({ id: s.id, label: s.name })),
              ]}
              value={filter.siteId}
              onChange={(id) => setFilter({ siteId: id })}
              placeholder="All Sites"
            />
          </div>
        )}
        <div className="relative">
          <input
            type="text"
            className="input pl-9 w-52 !py-1.5 !text-sm"
            placeholder="Search by name or ID…"
            value={workerSearch}
            onChange={(e) => setWorkerSearch(e.target.value)}
          />
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          {workerSearch && (
            <button className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setWorkerSearch('')}>×</button>
          )}
        </div>
        {hasPermission('worker_site_assignment') && (
          <button className="btn-secondary text-sm" onClick={openBulkAssign}>Bulk Assign to Site</button>
        )}
      </div>

      {/* ── Workers Table ──────────────────────────────────────────────────── */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Employee ID</th>
              <th>Type</th>
              <th>Role</th>
              <th>Assigned Sites</th>
              <th>Permission Group</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedWorkers.map((w) => (
              <tr key={w.id}>
                <td className="font-medium">{w.name}</td>
                <td className="text-gray-500 font-mono text-sm">{w.employeeId}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                    w.employeeType === 'SPTL'
                      ? 'bg-blue-100 text-blue-700 border-blue-200'
                      : 'bg-orange-100 text-orange-700 border-orange-200'
                  }`}>
                    {w.employeeType || 'SPTL'}
                  </span>
                </td>
                <td className="text-gray-500">{w.role || '—'}</td>
                <td>
                  {w.siteAssignments?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {w.siteAssignments.map((a) => (
                        <span key={a.siteId ?? a.id} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">
                          {a.site?.name}{a.assignmentType === 'GUEST' ? ' (Guest)' : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-300 text-sm">None</span>
                  )}
                </td>
                <td>
                  {w.employeeType === 'CONTRACT' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium border bg-gray-100 text-gray-400 border-gray-200">
                      N/A
                    </span>
                  ) : (
                    <button
                      className="flex items-center gap-1.5 group"
                      onClick={() => openDetail(w, 'permissions')}
                      title="Click to change permission group"
                    >
                      {w.permissionGroup ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium border group-hover:ring-2 group-hover:ring-indigo-300 transition-all ${
                          w.permissionGroup.isDefault
                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-indigo-100 text-indigo-700 border-indigo-200'
                        }`}>
                          {w.permissionGroup.name}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium border bg-gray-100 text-gray-400 border-gray-200 group-hover:ring-2 group-hover:ring-indigo-300 transition-all">
                          No group
                        </span>
                      )}
                      <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 text-gray-300 group-hover:text-indigo-500 flex-shrink-0">
                        <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z"/>
                      </svg>
                    </button>
                  )}
                </td>
                <td><StatusBadge status={w.isActive ? 'active' : 'inactive'} /></td>
                <td>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="text-green-600 hover:underline text-sm font-medium"
                      onClick={() => openDetail(w)}
                    >
                      View
                    </button>
                    {!isStorekeeper && hasPermission('worker_site_assignment') && (
                      <button className="text-purple-600 hover:underline text-sm" onClick={() => openAssignments(w)}>
                        Assign
                      </button>
                    )}
                    {!isStorekeeper && hasPermission('worker_site_assignment') && w.siteAssignments?.length > 0 && (
                      <button className="text-teal-600 hover:underline text-sm" onClick={() => openTransfer(w)}>
                        Transfer
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {displayedWorkers.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400">
                {workerSearch
                  ? `No ${workerTypeTab === 'sptl' ? 'SPTL employees' : 'contract workers'} match "${workerSearch}"`
                  : `No ${workerTypeTab === 'sptl' ? 'SPTL employees' : 'contract workers'} found`}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}


      {/* ════════════════════════════════════════════════════════════════════════
          WORKER DETAIL MODAL — read-only cross-site view
      ════════════════════════════════════════════════════════════════════════ */}
      {detailWorker && (
        <Modal
          title={detailWorker.name}
          subtitle={`${detailWorker.employeeId}${detailWorker.role ? ' · ' + detailWorker.role : ''}`}
          onClose={() => { setDetailWorker(null); setError(''); }}
          wide
        >
          {/* ── Tab Bar ────────────────────────────────────────────────────── */}
          <div className="flex gap-1 mb-4 border-b border-gray-200 -mt-1 pb-0">
            {[
              { key: 'overview',     label: 'Overview' },
              { key: 'materials',    label: `Materials (${allWorkerKits.length})` },
              { key: 'stationery',   label: `Stationery (${allStatIssuances.length})` },
              { key: 'deductions',   label: `Deductions (${workerDeductions.length})` },
              { key: 'sites',        label: 'Site History' },
              { key: 'permissions',  label: 'Permission Group' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setDetailTab(t.key)}
                className={`px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${
                  detailTab === t.key
                    ? 'border-indigo-600 text-indigo-700 bg-indigo-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loadingDetail ? (
            <div className="py-12 text-center text-sm text-gray-400">Loading history…</div>
          ) : (
            <>
              {/* ══ OVERVIEW TAB ══════════════════════════════════════════════ */}
              {detailTab === 'overview' && (
                <>
                  <SectionHeader title="Site Assignments" />
                  {detailWorker.siteAssignments?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {detailWorker.siteAssignments.map((a) => (
                        <span key={a.siteId ?? a.id} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-md font-medium">
                          {a.site?.name}{a.assignmentType === 'GUEST' ? ' (Guest)' : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-5">No site assignments yet</p>
                  )}

                  <SectionHeader title={`Active Materials (${matIssued.length})`} />
                  {matIssued.length === 0 ? (
                    <p className="text-sm text-gray-400 mb-5">No materials currently issued</p>
                  ) : (
                    <div className="space-y-2 mb-5">
                      {matIssued.map((wk) => {
                        const outstanding    = wk.quantity - (wk.returnedQuantity || 0);
                        const withinValidity = wk.validityEndsAt && new Date(wk.validityEndsAt) > now;
                        const validityExpired = wk.validityEndsAt && new Date(wk.validityEndsAt) <= now;
                        const isPaidItem     = (wk._count?.salaryDeductions || 0) > 0;
                        return (
                          <div key={wk.id} className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="font-medium text-sm text-gray-900">{wk.kit?.name}</p>
                                  {isPaidItem && (
                                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">Paid Item</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Qty out: <strong>{outstanding}</strong> · Issued {new Date(wk.issuedDate).toLocaleDateString('en-GB')}
                                  {wk.inventory?.site?.name ? ` · ${wk.inventory.site.name}` : ''}
                                </p>
                                {wk.materialUnit && (
                                  <p className="text-xs mt-0.5">
                                    Material ID:{' '}
                                    <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(wk.materialUnit.id)}>
                                      {wk.materialUnit.materialIdCode}
                                    </button>
                                  </p>
                                )}
                                {wk.validityEndsAt && (
                                  <p className={`text-xs mt-0.5 ${withinValidity ? 'text-indigo-600' : 'text-orange-500'}`}>
                                    {withinValidity
                                      ? `Valid until ${new Date(wk.validityEndsAt).toLocaleDateString('en-GB')}`
                                      : `Validity expired ${new Date(wk.validityEndsAt).toLocaleDateString('en-GB')}`}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Active</span>
                                {withinValidity && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">In validity</span>}
                                {validityExpired && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Expired</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <SectionHeader title={`Active Durables (${durableIss.length})`} />
                  {durableIss.length === 0 ? (
                    <p className="text-sm text-gray-400 mb-5">No durable stationery currently issued</p>
                  ) : (
                    <div className="space-y-2 mb-5">
                      {durableIss.map((iss) => {
                        const isPaidItem = (iss._count?.deductions || 0) > 0;
                        return (
                          <div key={iss.id} className="flex items-start justify-between p-3 rounded-lg border border-purple-100 bg-purple-50">
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-medium text-sm text-gray-900">{iss.item?.name}</p>
                                {isPaidItem && <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">Paid Item</span>}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Qty: <strong>{iss.quantity}</strong> · {new Date(iss.issuedDate).toLocaleDateString('en-GB')}
                                {iss.site?.name ? ` · ${iss.site.name}` : ''}
                              </p>
                              {iss.materialUnit && (
                                <p className="text-xs mt-0.5">
                                  Material ID:{' '}
                                  <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(iss.materialUnit.id)}>
                                    {iss.materialUnit.materialIdCode}
                                  </button>
                                </p>
                              )}
                            </div>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Active</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <SectionHeader title={`Active Consumables (${consumableIss.length})`} />
                  {consumableIss.length === 0 ? (
                    <p className="text-sm text-gray-400 mb-2">No consumable stationery currently issued</p>
                  ) : (
                    <div className="space-y-2">
                      {consumableIss.map((iss) => (
                        <div key={iss.id} className="flex items-start justify-between p-3 rounded-lg border border-green-100 bg-green-50">
                          <div>
                            <p className="font-medium text-sm text-gray-900">{iss.item?.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Qty: <strong>{iss.quantity}</strong> · {new Date(iss.issuedDate).toLocaleDateString('en-GB')}
                              {iss.site?.name ? ` · ${iss.site.name}` : ''}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded-full border border-green-100">Consumed</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400 text-center">
                      To issue or return items, go to <span className="font-medium text-indigo-600">Inventory → Workers</span> tab.
                    </p>
                  </div>
                </>
              )}

              {/* ══ MATERIAL HISTORY TAB ═════════════════════════════════════ */}
              {detailTab === 'materials' && (
                <>
                  {allWorkerKits.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No material issuance history</p>
                  ) : (
                    <div className="space-y-2">
                      {allWorkerKits.map((wk) => {
                        const outstanding    = wk.quantity - (wk.returnedQuantity || 0);
                        const withinValidity = wk.validityEndsAt && new Date(wk.validityEndsAt) > now;
                        const isPaidItem     = (wk._count?.salaryDeductions || 0) > 0;
                        return (
                          <div key={wk.id} className={`p-3 rounded-lg border ${wk.isReturned ? 'border-gray-200 bg-gray-50' : 'border-indigo-100 bg-indigo-50'}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="font-medium text-sm text-gray-900">{wk.kit?.name}</p>
                                  {isPaidItem && <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">Paid Item</span>}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Issued: <strong>{wk.quantity}</strong> · Returned: <strong>{wk.returnedQuantity || 0}</strong>
                                  {outstanding > 0 && <span className="text-indigo-600 ml-1">(out: {outstanding})</span>}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Issued on {new Date(wk.issuedDate).toLocaleDateString('en-GB')}
                                  {wk.inventory?.site?.name ? ` · ${wk.inventory.site.name}` : ''}
                                  {wk.returnedDate ? ` · Returned ${new Date(wk.returnedDate).toLocaleDateString('en-GB')}` : ''}
                                </p>
                                {wk.materialUnit && (
                                  <p className="text-xs mt-0.5">
                                    Material ID:{' '}
                                    <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(wk.materialUnit.id)}>
                                      {wk.materialUnit.materialIdCode}
                                    </button>
                                  </p>
                                )}
                                {wk.validityEndsAt && (
                                  <p className={`text-xs mt-0.5 ${withinValidity ? 'text-indigo-500' : 'text-gray-400'}`}>
                                    Validity: {new Date(wk.validityEndsAt).toLocaleDateString('en-GB')}
                                    {withinValidity ? ' (active)' : ' (expired)'}
                                  </p>
                                )}
                                {wk.returnCondition && (
                                  <p className={`text-xs mt-0.5 font-medium ${wk.returnCondition === 'DAMAGED' ? 'text-red-500' : 'text-green-600'}`}>
                                    Condition: {wk.returnCondition}
                                  </p>
                                )}
                                {wk.remarks && <p className="text-xs text-gray-400 italic mt-0.5">{wk.remarks}</p>}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                {wk.isReturned
                                  ? <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Returned</span>
                                  : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Active</span>
                                }
                                {isPaidItem && <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">Deducted</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ══ STATIONERY HISTORY TAB ═══════════════════════════════════ */}
              {detailTab === 'stationery' && (
                <>
                  {allStatIssuances.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No stationery issuance history</p>
                  ) : (
                    <div className="space-y-2">
                      {allStatIssuances.map((iss) => {
                        const isPaidItem   = (iss._count?.deductions || 0) > 0;
                        const isDurable    = iss.item?.category === 'DURABLE';
                        const borderColor  = iss.isReturned ? 'border-gray-200 bg-gray-50' : (isDurable ? 'border-purple-100 bg-purple-50' : 'border-green-100 bg-green-50');
                        return (
                          <div key={iss.id} className={`p-3 rounded-lg border ${borderColor}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <p className="font-medium text-sm text-gray-900">{iss.item?.name}</p>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isDurable ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                    {iss.item?.category}
                                  </span>
                                  {isPaidItem && <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">Paid Item</span>}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Qty: <strong>{iss.quantity}</strong>
                                  {iss.returnedQty > 0 ? ` · Returned: ${iss.returnedQty}` : ''}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Issued {new Date(iss.issuedDate).toLocaleDateString('en-GB')}
                                  {iss.returnedDate ? ` · Returned ${new Date(iss.returnedDate).toLocaleDateString('en-GB')}` : ''}
                                  {iss.site?.name ? ` · ${iss.site.name}` : ''}
                                </p>
                                {iss.materialUnit && (
                                  <p className="text-xs mt-0.5">
                                    Material ID:{' '}
                                    <button className="font-mono text-indigo-600 hover:underline" onClick={() => openMaterialHistory(iss.materialUnit.id)}>
                                      {iss.materialUnit.materialIdCode}
                                    </button>
                                  </p>
                                )}
                                {iss.repairStatus && (
                                  <p className={`text-xs mt-0.5 font-medium ${
                                    iss.repairStatus === 'UNDER_REPAIR' ? 'text-amber-600' :
                                    iss.repairStatus === 'REPAIRED'     ? 'text-green-600' : 'text-red-500'
                                  }`}>
                                    Repair: {iss.repairStatus.replace('_', ' ')}
                                  </p>
                                )}
                                {iss.returnCondition && (
                                  <p className={`text-xs mt-0.5 ${iss.returnCondition === 'DAMAGED' ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                    Condition: {iss.returnCondition}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                {iss.isReturned
                                  ? <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Returned</span>
                                  : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Active</span>
                                }
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ══ DEDUCTIONS TAB ═══════════════════════════════════════════ */}
              {detailTab === 'deductions' && (
                <>
                  {/* Always-visible totals — independent of the cycle filter below */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3">
                      <p className="text-xs text-indigo-600 font-medium">Current Cycle</p>
                      <p className="text-lg font-bold text-indigo-800">{fmtCurrency(currentCycleTotal)}</p>
                      <p className="text-xs text-indigo-400 mt-0.5">{deductionCycles[0]?.label}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                      <p className="text-xs text-red-600 font-medium">All-Time Total</p>
                      <p className="text-lg font-bold text-red-800">{fmtCurrency(allTimeTotal)}</p>
                      <p className="text-xs text-red-400 mt-0.5">{workerDeductions.length} record{workerDeductions.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Cycle filter */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 font-medium mb-1 block">View</label>
                    <select
                      className="input w-full sm:w-72"
                      value={deductionCycleFilter}
                      onChange={(e) => setDeductionCycleFilter(e.target.value)}
                    >
                      <option value="current">Current Cycle ({deductionCycles[0]?.label})</option>
                      {deductionCycles.slice(1).map((c) => (
                        <option key={c.cycleStart} value={c.cycleStart}>{c.label}</option>
                      ))}
                      <option value="all">All-Time</option>
                    </select>
                  </div>

                  {displayedDeductions.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No salary deductions for this period</p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {displayedDeductions.map((d) => {
                          const materialName = d.workerKit?.kit?.name || d.stationeryIssuance?.item?.name || '—';
                          return (
                            <div key={d.id} className="p-3 rounded-lg border border-red-100 bg-red-50">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <p className="font-medium text-sm text-gray-900">{materialName}</p>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                      d.deductionType === 'MATERIAL_REISSUE'
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : d.deductionType === 'MAX_QUANTITY_EXCEEDED'
                                        ? 'bg-rose-100 text-rose-700'
                                        : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {d.deductionType === 'MATERIAL_REISSUE'
                                        ? 'Re-issue'
                                        : d.deductionType === 'MAX_QUANTITY_EXCEEDED'
                                        ? 'Max Quantity Exceeded'
                                        : 'Durable No Return'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5">{d.reason}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {new Date(d.createdAt).toLocaleDateString('en-GB')}
                                    {d.cycleStart && ` · Cycle: ${new Date(d.cycleStart).toLocaleDateString('en-GB')}`}
                                  </p>
                                </div>
                                <p className="text-base font-bold text-red-600 flex-shrink-0">₹{parseFloat(d.amount).toFixed(2)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ══ SITE HISTORY TAB ═════════════════════════════════════════ */}
              {detailTab === 'sites' && (
                <>
                  {workerAssignHistory.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No site assignment history</p>
                  ) : (
                    <div className="space-y-2">
                      {workerAssignHistory.map((a) => (
                        <div key={a.id} className="flex items-start justify-between p-3 rounded-lg border border-blue-100 bg-blue-50">
                          <div>
                            <p className="font-medium text-sm text-gray-900">{a.site?.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Assigned {new Date(a.assignedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {a.assignedByUsername ? ` by ${a.assignedByUsername}` : ''}
                            </p>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Active</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ══ PERMISSION GROUP TAB ═════════════════════════════════════ */}
              {detailTab === 'permissions' && (
                <div className="space-y-4">
                  {detailWorker.employeeType === 'CONTRACT' ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center">
                      <p className="text-sm font-semibold text-slate-700">Contract Worker</p>
                      <p className="text-xs text-slate-500 mt-1.5 max-w-sm mx-auto">
                        Contract workers don't have user accounts and cannot be assigned permission groups.
                        Only SPTL employees are assigned application permissions.
                      </p>
                    </div>
                  ) : pgLoading ? (
                    <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
                  ) : (
                    <>
                      {/* Permission groups are assigned directly to the Worker record now —
                          a linked Portal user account isn't required. This note is purely
                          informational (Portal login is a separate concern from module access). */}
                      {workerPG?.linkedUserId === null && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          No Portal user account matches this worker's Employee ID ({detailWorker.employeeId}) — the permission group below still applies to their MaterialWatch access.
                        </div>
                      )}

                      {/* Current group */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Current Permission Group</p>
                        {workerPG?.group ? (
                          <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-900">{workerPG.group.name}</p>
                              {workerPG.group.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{workerPG.group.description}</p>
                              )}
                              {workerPG.group.isDefault && (
                                <span className="text-xs text-amber-600 font-medium">
                                  ★ {workerPG.group.isImplicitDefault ? 'Inherited (Default group)' : 'Default group'}
                                </span>
                              )}
                            </div>
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                              {workerPG.group.permissions?.length ?? 0} permissions
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">Not assigned to any group</p>
                        )}
                      </div>

                      {/* Change group */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Change Group</p>
                        {workerPG?.group?.isSynthetic ? (
                          // Admin Access is computed from the linked Portal account's
                          // super_admin role, not stored — there's nothing to change here.
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 flex items-center gap-2">
                            <span>🔒</span>
                            <span>Super Admins have full access automatically — no group to assign.</span>
                          </div>
                        ) : workerPG?.group?.isRestricted ? (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 flex items-center gap-2">
                            <span>🔒</span>
                            <span>{workerPG.group.name} cannot be changed.</span>
                          </div>
                        ) : workerPG?.group?.isSystem && !(workerPG.group.permissions || []).every((pe) => hasPermission(pe.permission)) ? (
                          // Current group is Full Access (or similar) but the viewer doesn't hold
                          // it themselves — same "locked" treatment as a restricted group.
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 flex items-center gap-2">
                            <span>🔒</span>
                            <span>You cannot edit the {workerPG.group.name} permission.</span>
                          </div>
                        ) : (
                          <>
                            <select
                              className="input w-full"
                              value={pgGroupId}
                              onChange={(e) => setPgGroupId(e.target.value)}
                            >
                              {allGroups
                                .filter((g) => {
                                  if (g.isRestricted) return false;
                                  // Only Full Access (isSystem) is gated — hidden entirely from
                                  // anyone who doesn't already hold it themselves.
                                  if (g.isSystem) return (g.permissions || []).every((pe) => hasPermission(pe.permission));
                                  // Restricted actor: only their own group's assignableGroups list —
                                  // except the worker's CURRENT group, always kept visible so the
                                  // select never renders blank for a group outside that list.
                                  if (actorAssignableIds && !actorAssignableIds.has(g.id) && g.id !== pgCurrentGroup?.id) return false;
                                  return true;
                                })
                                .map((g) => (
                                  <option key={g.id} value={g.id}>
                                    {g.name}{g.isDefault ? ' (Default)' : ''}
                                  </option>
                                ))}
                            </select>
                          </>
                        )}
                      </div>

                      {pgError && (
                        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">{pgError}</p>
                      )}

                      {!isPgLocked && (
                        <div className="flex justify-end pt-1 border-t border-gray-100">
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={handleSavePG}
                            disabled={pgSaving}
                          >
                            {pgSaving ? 'Saving…' : 'Save Group'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </Modal>
      )}

      {/* Site Assignments */}
      {assignmentWorker && (
        <Modal
          title={`Site Assignments — ${assignmentWorker.name}`}
          subtitle={assignmentWorker.employeeId}
          onClose={() => { setAssignmentWorker(null); setError(''); }}
        >
          <div className="space-y-4">
            {assignments.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No site assignments yet</p>
            ) : (
              <div className="space-y-2">
                {assignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {a.site?.name}
                        {a.assignmentType === 'GUEST' && (
                          <span className="ml-1.5 text-xs font-medium text-slate-500">(Guest)</span>
                        )}
                      </p>
                      {a.assignedByUsername && <p className="text-xs text-gray-400">Assigned by {a.assignedByUsername}</p>}
                    </div>
                    <button className="text-red-500 hover:text-red-700 text-xs font-medium" onClick={() => handleRemoveAssignment(assignmentWorker.id, a.siteId)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {unassignedSites.length > 0 && (
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <div className="flex-1">
                  <SearchableSelect
                    options={unassignedSites.map((s) => ({ id: s.id, label: s.name }))}
                    value={assignSiteId}
                    onChange={setAssignSiteId}
                    placeholder="Add site assignment…"
                  />
                </div>
                <button type="button" className="btn-primary" onClick={handleAddAssignment} disabled={!assignSiteId}>Add</button>
              </div>
            )}
            {error && <p className="text-red-600 text-sm">{error}</p>}
          </div>
        </Modal>
      )}

      {/* Bulk Assign to Site */}
      {bulkAssignOpen && (
        <Modal
          title="Bulk Assign Workers to a Site"
          subtitle="Paste names or employee IDs (one per line, or comma-separated) — this sets their site, replacing any other site they're currently on"
          onClose={() => setBulkAssignOpen(false)}
          wide
        >
          <div className="space-y-4">
            {!bulkResult ? (
              <>
                <textarea
                  className="input w-full font-mono text-sm"
                  rows={5}
                  placeholder={'e.g.\nEMP1023\nJohn Doe\nEMP1050, Priya Singh'}
                  value={bulkPasteText}
                  onChange={(e) => setBulkPasteText(e.target.value)}
                />
                <button type="button" className="btn-secondary" onClick={findBulkMatches} disabled={!bulkPasteText.trim()}>
                  Find Matches
                </button>

                {bulkNotFoundTokens.length > 0 && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
                    <p className="font-medium mb-1">Not found ({bulkNotFoundTokens.length}):</p>
                    <p className="font-mono text-xs">{bulkNotFoundTokens.join(', ')}</p>
                  </div>
                )}

                {bulkMatchedWorkers.length > 0 && (
                  <>
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={bulkSelectedIds.size === bulkMatchedWorkers.length}
                          onChange={toggleBulkSelectAll}
                        />
                        Select all ({bulkMatchedWorkers.length} matched)
                      </label>
                      <span className="text-sm text-gray-500">{bulkSelectedIds.size} selected</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-2">
                      {bulkMatchedWorkers.map((w) => (
                        <label key={w.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 text-sm">
                          <input
                            type="checkbox"
                            checked={bulkSelectedIds.has(w.id)}
                            onChange={() => toggleBulkSelected(w.id)}
                          />
                          <span className="font-medium">{w.name}</span>
                          <span className="text-gray-400 font-mono text-xs">{w.employeeId}</span>
                        </label>
                      ))}
                    </div>

                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <div className="flex-1">
                        <SearchableSelect
                          options={sites.map((s) => ({ id: s.id, label: s.name }))}
                          value={bulkSiteId}
                          onChange={setBulkSiteId}
                          placeholder="Select site…"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleBulkAssignSubmit}
                        disabled={!bulkSiteId || bulkSelectedIds.size === 0 || bulkSubmitting}
                      >
                        {bulkSubmitting ? 'Assigning…' : `Assign ${bulkSelectedIds.size}`}
                      </button>
                    </div>
                  </>
                )}
                {bulkError && <p className="text-red-600 text-sm">{bulkError}</p>}
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-800">
                  {bulkResult.assigned.length + (bulkResult.reassigned?.length || 0)} of{' '}
                  {bulkResult.assigned.length + (bulkResult.reassigned?.length || 0) + bulkResult.alreadyAssigned.length} set to {bulkResult.site?.name}.
                </p>
                {bulkResult.assigned.length > 0 && (
                  <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-sm text-green-800">
                    <p className="font-medium mb-1">Assigned ({bulkResult.assigned.length}):</p>
                    <p>{bulkResult.assigned.map((w) => w.name).join(', ')}</p>
                  </div>
                )}
                {bulkResult.reassigned?.length > 0 && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
                    <p className="font-medium mb-1">Site changed ({bulkResult.reassigned.length}):</p>
                    <p>{bulkResult.reassigned.map((w) => `${w.name} (was ${w.previousSiteName})`).join(', ')}</p>
                  </div>
                )}
                {bulkResult.alreadyAssigned.length > 0 && (
                  <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600">
                    <p className="font-medium mb-1">Already at this site ({bulkResult.alreadyAssigned.length}):</p>
                    <p>{bulkResult.alreadyAssigned.map((w) => w.name).join(', ')}</p>
                  </div>
                )}
                <button type="button" className="btn-secondary" onClick={openBulkAssign}>Assign more</button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Transfer Worker */}
      {transferWorker && (() => {
        const currentSites     = transferWorker.siteAssignments || [];
        const currentSiteIds   = new Set(currentSites.map((a) => a.siteId));
        const destinationSites = sites.filter((s) => !currentSiteIds.has(s.id));
        const fromSite         = currentSites.find((a) => String(a.siteId) === String(transferForm.fromSiteId));
        return (
          <Modal
            title={`Transfer Worker — ${transferWorker.name}`}
            subtitle={`Employee ID: ${transferWorker.employeeId}`}
            onClose={() => { setTransferWorker(null); setError(''); }}
          >
            <form onSubmit={handleTransferSubmit} className="space-y-4">
              <div>
                <p className="label mb-1">Currently Assigned To</p>
                <div className="flex flex-wrap gap-1.5">
                  {currentSites.map((a) => (
                    <span key={a.siteId} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-md font-medium">{a.site?.name}</span>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Transfer From</label>
                {currentSites.length === 1 ? (
                  <div className="input bg-gray-50 cursor-not-allowed text-gray-700">{currentSites[0].site?.name}</div>
                ) : (
                  <SearchableSelect
                    options={currentSites.map((a) => ({ id: a.siteId, label: a.site?.name }))}
                    value={transferForm.fromSiteId}
                    onChange={(id) => setTransferForm({ ...transferForm, fromSiteId: id, toSiteId: '' })}
                    placeholder="Select source site…"
                    required
                  />
                )}
              </div>
              <div>
                <label className="label">Transfer To</label>
                {destinationSites.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Worker is already assigned to all available sites.
                  </p>
                ) : (
                  <SearchableSelect
                    options={destinationSites.map((s) => ({ id: s.id, label: s.name }))}
                    value={transferForm.toSiteId}
                    onChange={(id) => setTransferForm({ ...transferForm, toSiteId: id })}
                    placeholder="Select destination site…"
                    required
                  />
                )}
              </div>
              {transferForm.fromSiteId && transferForm.toSiteId && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
                  <p className="font-semibold mb-1">What happens on transfer:</p>
                  <ul className="list-disc list-inside space-y-1 text-teal-700">
                    <li>Worker moves from <strong>{fromSite?.site?.name}</strong> to <strong>{sites.find((s) => s.id === parseInt(transferForm.toSiteId))?.name}</strong></li>
                    <li>All currently issued materials remain with the worker</li>
                    <li>Active stationery issuances move to the new site</li>
                    <li>Validity periods and issue history are preserved</li>
                  </ul>
                </div>
              )}
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <ModalActions onCancel={() => { setTransferWorker(null); setError(''); }} submitLabel="Confirm Transfer" disabled={!transferForm.fromSiteId || !transferForm.toSiteId} />
            </form>
          </Modal>
        );
      })()}

      {/* Material ID lifecycle history modal */}
      {historyModal && (
        <Modal
          title="Material History"
          subtitle={historyModal !== 'loading' ? historyModal.unit?.materialIdCode : undefined}
          onClose={() => setHistoryModal(null)}
        >
          {historyModal === 'loading' ? (
            <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name} · {historyModal.unit?.site?.name}
              </p>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {historyModal.events.map((e) => (
                  <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                      {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                      {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                    </div>
                    <p className="text-xs text-gray-400 whitespace-nowrap">
                      {new Date(e.occurredAt).toLocaleString('en-GB')}
                    </p>
                  </div>
                ))}
                {historyModal.events.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
                )}
              </div>
              <MaterialInfoPanel materialUnitId={historyModal.unit?.id} unitKitId={historyModal.unit?.kitId} unitItItemId={historyModal.unit?.itItemId} />
            </div>
          )}
        </Modal>
      )}

    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

const Modal = ({ title, subtitle, children, onClose, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className={`bg-white rounded-xl shadow-xl p-6 w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel, disabled = false }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary" disabled={disabled}>{submitLabel}</button>
  </div>
);

const SectionHeader = ({ title }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2 mt-5 first:mt-0">{title}</p>
);
